'use client';

/* eslint-disable no-underscore-dangle */
import React, { useMemo, useCallback } from 'react';
import { useHits, useInstantSearch } from 'react-instantsearch';
import Link from 'components/consumer/Link';
import JBImage from 'components/essentials/JBImage';
import ProductCardPriceUI from 'components/consumer/product-card/ProductCardPrice/ProductCardPriceUI';
import useProductCardDataNext from 'hooks/useProductCardDataNext';
import useUserCurrentPageType from 'hooks/useUserCurrentPageType';
import avo from 'analytics/avo';
import { getVariantsByMatchedColorFirst } from 'components/consumer/consumer-search/advanced-search-next/components/AdvancedSearchProductHits/utils';
import SeeAllButtonNext from '../SeeAllButtonNext';

/**
 * Search Modal Product List - Mobile view (Next version)
 * Uses React InstantSearch v7 hooks instead of legacy connectStateResults
 * Renders products in a vertical list layout for mobile search modal
 */
const SearchModalProductListMobileNext = ({ toggleSearchOpen }) => {
  const { items: hits } = useHits();
  const { results } = useInstantSearch();
  const currentPage = useUserCurrentPageType();

  const limitedHits = useMemo(() => hits.slice(0, 3), [hits]);

  const productIds = useMemo(
    () => limitedHits.map(hit => parseInt(hit.product_id || hit.objectID, 10)),
    [limitedHits]
  );

  const { products, loading } = useProductCardDataNext({ productIds });

  const productsWithHighlightedVariantsFirst = useMemo(
    () =>
      products.map(product => ({
        ...product,
        variantsToShow: getVariantsByMatchedColorFirst(product, hits),
      })),
    [products, hits]
  );

  const handleProductClick = useCallback(() => {
    avo.productsSearched({
      query: results?.query ?? '',
      location: currentPage,
      searchTarget: avo.SearchTarget.PRODUCT_GRID,
    });
    toggleSearchOpen?.();
  }, [results?.query, currentPage, toggleSearchOpen]);

  if (!limitedHits.length) {
    return null;
  }

  if (loading) {
    return (
      <div className="instant-search-form__product-results flex-1 [border-right:1px_solid_#f1f1f1] pt-5 pr-8 pb-10">
        <div className="text-brand block mt-[15px] mb-[7px] text-base font-bold">
          Products
        </div>
        {[0, 1, 2].map(i => (
          <div
            key={i}
            className="flex items-center py-4 [border-bottom:1px_solid_#f1f1f1] last:[border-bottom:0]"
          >
            <div className="mr-4 w-[100px] h-[100px] bg-gray-100 animate-pulse rounded" />
            <div className="flex-1">
              <div className="h-4 bg-gray-100 animate-pulse rounded mb-2 w-3/4" />
              <div className="h-3 bg-gray-100 animate-pulse rounded w-1/4" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="instant-search-form__product-results flex-1 [border-right:1px_solid_#f1f1f1] pt-5 pr-8 pb-10 [&_.ais-Hits-list]:flex [&_.ais-Hits-item]:p-2.5">
      <div className="text-brand block mt-[15px] mb-[7px] text-base font-bold">
        Products
      </div>
      {productsWithHighlightedVariantsFirst.map(product => (
        <Link
          key={product.id}
          onClick={handleProductClick}
          to={product.variantsToShow?.[0]?.path ?? ''}
          className="text-base flex items-center font-bold text-gray px-0 py-4 [border-bottom:1px_solid_#f1f1f1] no-underline last:[border-bottom:0] [&_img]:object-contain [text-decoration:none]"
        >
          <div className="mr-4 w-[100px] flex justify-center items-center">
            <JBImage
              className="object-contain m-5 opacity-100 max-h-full max-w-full"
              fit="clip"
              height={265}
              width={265}
              lazy={false}
              loader={false}
              src={product.variantsToShow?.[0]?.image ?? ''}
              trim="color"
              trimPad={20}
            />
          </div>
          <div>
            {product.name}
            <ProductCardPriceUI
              originalPrice={product.variantsToShow?.[0]?.originalPrice}
              salePrice={product.variantsToShow?.[0]?.price}
            />
          </div>
        </Link>
      ))}

      <div className="flex mt-5 justify-start [&_a]:[text-decoration:none]">
        <SeeAllButtonNext
          toggleSearchOpen={toggleSearchOpen}
          variant="mobile"
        />
      </div>
    </div>
  );
};

export default SearchModalProductListMobileNext;
